/**
 * 보험금 청구 플로우( support/claims — Claims.js )에서
 * 화면으로 입력·선택되는 값 + 서버가 청구일·사고일에서 채우는 파생 bindKey + 서명.
 *
 * API extraFields에는 고정값·숨김 항목 등이 더 들어갈 수 있으나, 청구서식 UI의
 * 청록 강조·「직접입력필드」에는 아래만 포함한다. (주석/히든·자동만 반영 키 제외)
 */
export const CLAIM_FLOW_COLLECTED_BIND_KEYS = Object.freeze([
  // 신청 본문
  'customerName',
  'customerBirthDate',
  'customerPhone',
  'memo',
  'claimDate',
  'claimDateYear',
  'claimDateYear2',
  'claimDateYear20NN',
  'claimDateYearNN',
  'claimDateMonth',
  'claimDateDay',
  'accidentDate',
  'accidentDateYear',
  'accidentDateYear2',
  'accidentDateMonth',
  'accidentDateDay',
  // STEP 5 서명
  'customerSignature',
  'contractHolderSignature',
  'beneficiarySignature',
  // 피보험자·주소(통합 한 줄은 customerAddress)
  'customerResidentNumber',
  'customerResidentNumber_1',
  'customerResidentNumber_2',
  'customerResidentNumber_rearDigit1',
  'customerResidentNumber_rearDigits2',
  'customerPhone_1',
  'customerPhone_2',
  'customerPhone_3',
  'customerEmail',
  'customerAddress',
  'customerWorkplace',
  'customerOccupation',
  'medicalBenefitRecipient',
  'medicalBenefitRecipient_Y',
  'medicalBenefitRecipient_N',
  'drivingLicense',
  'drivingLicense_Y',
  'drivingLicense_N',
  // 계약자
  'contractHolderSameAsInsured',
  'contractHolderName',
  'contractHolderResidentNumber',
  'contractHolderResidentNumber_1',
  'contractHolderResidentNumber_2',
  'contractHolderResidentNumber_rearDigit1',
  'contractHolderResidentNumber_rearDigits2',
  'contractHolderPhone',
  'contractHolderPhone_1',
  'contractHolderPhone_2',
  'contractHolderPhone_3',
  // 수익자·보상 안내(라디오·텍스트 입력)
  'beneficiaryName',
  'beneficiaryRelationToInsured',
  'beneficiaryResidentNo',
  'beneficiaryResidentNumber',
  'beneficiaryResidentNumber_1',
  'beneficiaryResidentNumber_2',
  'beneficiaryResidentNumber_rearDigit1',
  'beneficiaryResidentNumber_rearDigits2',
  'beneficiaryPhone',
  'beneficiaryPhone_1',
  'beneficiaryPhone_2',
  'beneficiaryPhone_3',
  'beneficiaryEmail',
  'beneficiaryAddress',
  'beneficiaryWorkplace',
  'beneficiaryOccupation',
  'beneficiarySameAsCustomer',
  'beneficiarySameAsContractHolder',
  'benefitNotificationRecipient',
  'benefitNotificationMethod',
  'benefitNotificationMethodOption',
  'benefitGuidePlannerName',
  'benefitGuidePlannerEmail',
  'benefitGuidePlannerResidentNumber',
  'benefitGuidePlannerResidentNumber_1',
  'benefitGuidePlannerResidentNumber_2',
  'beneficiaryIdentityType',
  'beneficiaryResidentIdIssueDate',
  'beneficiaryResidentIdIssueDateYear',
  'beneficiaryResidentIdIssueDateYear2',
  'beneficiaryResidentIdIssueDateMonth',
  'beneficiaryResidentIdIssueDateDay',
  'beneficiaryResidentIdIssuer',
  'beneficiaryDriverLicenseNumber',
  'beneficiaryDriverLicenseNumberRegion',
  'beneficiaryDriverLicenseNumberSecond',
  'beneficiaryDriverLicenseNumberThird',
  'beneficiaryDriverLicenseNumberFourth',
  'beneficiaryDriverLicenseSerialNumber',
  'beneficiaryDriverLicenseIssueDate',
  'beneficiaryDriverLicenseIssuer',
  // 보험금 수령(유형 + 일반 시 은행 정보)
  'accountPayoutType',
  'bankAutoTransfer',
  'bankPrepaidAccount',
  'bankName',
  'accountNumber',
  'accountHolder',
  // 사고·청구
  'accidentType',
  'accidentType_질병',
  'accidentType_재해',
  'accidentType_상해',
  'accidentType_교통사고',
  'accidentType_자살',
  'accidentType_기타',
  'claimType',
  'claimType_사망',
  'claimType_장해',
  'claimType_진단',
  'claimType_치과치료',
  'claimType_수술',
  'claimType_입원',
  'claimType_통원',
  'claimType_실손',
  'claimType_치료',
  'claimType_기타',
  'claimType_기타입력',
  'claimScope',
  'partialClaimReason',
  'claimCoverage',
  'claimCoverageExclusion',
  'claimAmount',
  'claimContent',
  'accidentDetails',
  'accidentLocation',
  'accidentDateHour',
  'accidentDateMinute',
  'hospitalName',
  'diagnosis',
  'autoInsuranceProcessed',
  'autoInsuranceProcessed_Y',
  'autoInsuranceProcessed_N',
  'autoInsuranceCompany',
  'autoInsuranceContact',
  'workersCompInsuranceProcessed',
  'workersCompInsuranceProcessed_Y',
  'workersCompInsuranceProcessed_N',
  'vehicleNumber',
  'vehicleSeatPosition',
  'autoInsuranceClaimed',
  'proxyServiceRequested',
  'proxyServiceTarget',
  'accidentReceiptType',
  // 보험사별 추가 입력
  'virtualFaxNumber',
  'postalFaxNumber',
  'faxNumberSelection',
  'injuryDiseaseTreatmentSupport',
  'groupActualMedicalInsurance',
  'insuredJobDetail',
  'identityType',
  'residentIdIssueDate',
  'residentIdIssueDateYear',
  'residentIdIssueDateYear2',
  'residentIdIssueDateMonth',
  'residentIdIssueDateDay',
  'residentIdIssuer',
  'driverLicenseNumber',
  'driverLicenseNumberRegion',
  'driverLicenseNumberSecond',
  'driverLicenseNumberThird',
  'driverLicenseNumberFourth',
  'driverLicenseSerialNumber',
  'driverLicenseIssueDate',
  'driverLicenseIssuer',
  'minorBeneficiary',
  'guardianSelection',
  'guardianFatherName',
  'guardianFatherResidentNumber',
  'guardianFatherPhone',
  'guardianMotherName',
  'guardianMotherResidentNumber',
  'guardianMotherPhone',
  'petOwnerName',
  'petOwnerResidentNumber',
  'petOwnerResidentNumber_1',
  'petOwnerResidentNumber_2',
  'petOwnerPhone',
  'petOwnerPhone_1',
  'petOwnerPhone_2',
  'petOwnerPhone_3',
  'petOwnerAddress',
  'petName',
  'petBreed',
  'petGender',
  'petAge',
  'petBirthDate',
  'petBirthYear',
  'petBirthMonth',
  'petBirthDay',
  'petColor',
  'petReceiptType',
  'petDiagnosis',
  'petAccidentCauseDetail',
  'petNarrativeWho',
  'petNarrativeWhen',
  'petNarrativeWhere',
  'petNarrativeWhat',
  'petNarrativeHow',
  'petNarrativeWhy',
  'animalHospitalName',
  'animalHospitalPhone',
  'veterinarianName',
  'petDamageVictimPhone',
  'petDamageVictimName',
  'petDamageVictimAddress',
  'petDamageDescription',
  'petDamageDetails',
  'petDamageVictimNameOrObject',
  'petClaimCoverageMulti',
  'petDamageEtc',
  'animalHospitalVetInfo',
  'petTreatmentDateAndContent',
  'petClaimCoverage',
  'petLossType',
  'accidentLocationCity',
  'accidentLocationDistrict',
  'accidentLocationTown',
  'liabilityAccidentTypeDetail',
  'accountHolderResidentNumber',
  'accountHolderResidentNumber_1',
  'accountHolderResidentNumber_2',
  'accountHolderRelation',
  'guardianFatherResidentNumber_1',
  'guardianFatherResidentNumber_2',
  'guardianFatherPhone_1',
  'guardianFatherPhone_2',
  'guardianFatherPhone_3',
  'guardianMotherResidentNumber_1',
  'guardianMotherResidentNumber_2',
  'guardianMotherPhone_1',
  'guardianMotherPhone_2',
  'guardianMotherPhone_3',
  'printedAt',
  'printedAtYear',
  'printedAtMonth',
  'printedAtDay',
])

const FLOW_SET = new Set(CLAIM_FLOW_COLLECTED_BIND_KEYS)

export function isClaimFlowCollectedBindKey(bindKey) {
  if (bindKey == null) return false
  const k = String(bindKey).trim()
  return k !== '' && FLOW_SET.has(k)
}
